import { startWithPhraseIdeas } from './phrase_ideas_dialog';
import SpeakApiClient from './speak_api_client';
import { debounce } from '../../utils/callMetricsTracker';
import { historyFlagCountryCode, shouldShowLanguageFilter } from './history_language';
import { defaultSpeakTab } from '../../my_path/tab';

const I18N_SCOPE = 'javascript.speak_page';
// Call-mode picker tier per TTS provider. Inworld voices (Yuki) share the v2
// tier with ElevenLabs; keep in sync with ChatForm.call_mode_tier_for_voice.
const PROVIDER_VERSIONS = { openai: 'v1', elevenlabs: 'v2', inworld: 'v2', cartesia: 'v3' };

function t(key, defaultValue, options = {}) {
  if (!window.I18n) return defaultValue;

  return window.I18n.t(`${I18N_SCOPE}.${key}`, { defaultValue, ...options });
}

function buildElement(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = text;
  return element;
}

function replacePathId(template, id) {
  return (template || '/account/chats/:id').replace(':id', encodeURIComponent(id));
}

function humanize(value) {
  return String(value || '')
    .split('.')
    .pop()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
}

class SpeakPageService {
  // The tenant page meta; consumer pages keep the master avatar and sample paths.
  get selfHostedAssets() {
    return document.querySelector?.('meta[name="self-hosted-assets"]')?.content === 'true';
  }

  get privateVoiceSamples() {
    return document.querySelector?.('meta[name="private-voice-samples"]')?.content === 'true';
  }

  constructor(container) {
    this.container = container;
    this.config = this.readConfig();
    this.api = new SpeakApiClient(container.dataset.authToken);
    this.currentTab = 'browse';
    this.isBrowseTopLevel = true;
    this.reviewLoaded = false;
    this.historyLoaded = false;
    this.historyPage = 1;
    this.historyTotalPages = 1;
    this.loadedHistoryRows = [];
    this.historyQuery = '';
    this.historyLanguage = '';
    this.historyLanguages = [];
    this.historyRequestId = 0;
    this.currentRecommendation = null;
    this.currentLanguage = this.config.current_language;
    this.preferredDialect = this.config.preferred_dialect;
    this.currentVoiceId = this.config.current_voice?.id;
    this.sampleAudio = null;
    this.activeSampleVoiceId = null;
    this.activeSampleButton = null;
    this.sampleUrlCache = {};
    this.sampleRequestId = 0;

    this.bindTabs();
    this.bindHistorySearch();
    this.bindHistoryLoadMore();
    this.bindTodaysConversation();
    this.bindLanguageModal();
    this.bindVoicePicker();
    this.loadRecommendation();
    this.loadVoiceOptions();
    if (this.container.querySelector('.js-my-path-root')) {
      this.myPathInitialSettings = this.currentChatSettings();
      this.activateTab(defaultSpeakTab(true, window.location.search));
    }
  }

  readConfig() {
    const configEl = this.container.querySelector('.js-speak-page-config');
    if (!configEl) return {};

    try {
      return JSON.parse(configEl.textContent);
    } catch (error) {
      console.warn('Unable to parse speak page config', error);
      return {};
    }
  }

  bindTabs() {
    this.tabButtons = Array.from(this.container.querySelectorAll('.js-speak-tab-btn'));
    this.tabPanes = Array.from(this.container.querySelectorAll('.js-speak-tab-pane'));
    this.todayShell = this.container.querySelector('.js-todays-conversation-shell');

    this.tabButtons.forEach(button => {
      button.addEventListener('click', () => this.activateTab(button.dataset.speakTab));
    });

    if (this.container.querySelector('.js-my-path-root')) {
      this.tabButtons.forEach(button => {
        button.id = `speak-tab-${button.dataset.speakTab}`;
        button.setAttribute('aria-controls', `speak-pane-${button.dataset.speakTab}`);
      });
      this.tabPanes.forEach(pane => {
        pane.id = `speak-pane-${pane.dataset.speakPane}`;
        pane.setAttribute('role', 'tabpanel');
        pane.setAttribute('aria-labelledby', `speak-tab-${pane.dataset.speakPane}`);
      });
      this.tabButtons.forEach((button, index) => button.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabButtons.length;
        if (event.key === 'ArrowLeft') next = (index + this.tabButtons.length - 1) % this.tabButtons.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabButtons.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.tabButtons[next].focus();
        this.activateTab(this.tabButtons[next].dataset.speakTab);
      }));
    }

    this.container.querySelectorAll('.js-speak-switch-to-browse').forEach(button => {
      button.addEventListener('click', () => this.activateTab('browse'));
    });

    this.container.querySelectorAll('.js-speak-open-chat-settings').forEach(button => {
      button.addEventListener('click', () => {
        if (window.$) window.$('#chatOptionsModal').modal('show');
      });
    });

    document.addEventListener('speak:browse-subview-change', event => {
      this.isBrowseTopLevel = event.detail?.isTopLevel !== false;
      this.updateTodaysConversationVisibility();
    });

    this.updateTodaysConversationVisibility();
  }

  bindHistorySearch() {
    const search = this.container.querySelector('.js-speak-history-search');
    if (!search) return;

    const run = debounce(() => {
      this.historyQuery = search.value.trim();
      this.loadHistory(1);
    }, 300);
    search.addEventListener('input', run);
  }

  bindHistoryLoadMore() {
    this.container.querySelector('.js-speak-history-load-more')
      ?.addEventListener('click', () => this.loadHistory(this.historyPage + 1));
  }

  activateTab(tabName) {
    if (!tabName) return;

    this.currentTab = tabName;
    this.tabButtons.forEach(button => {
      const active = button.dataset.speakTab === tabName;
      button.classList.toggle('active', active);
      button.setAttribute('aria-selected', active.toString());
    });

    this.tabPanes.forEach(pane => {
      pane.classList.toggle('d-none', pane.dataset.speakPane !== tabName);
    });

    this.updateTodaysConversationVisibility();
    if (this.container.querySelector('.js-my-path-root')) {
      this.tabButtons.forEach(button => { button.tabIndex = button.dataset.speakTab === tabName ? 0 : -1; });
      if (tabName === 'my_path' && !this.myPathMount) {
        this.myPathMount = import('../../my_path/mount').then(({ mountMyPath }) => {
          if (this.destroyed || !this.container.isConnected) return;
          this.unmountMyPath = mountMyPath(this.container.querySelector('.js-my-path-root'), {
            isActive: () => this.currentTab === 'my_path',
            onExplore: topicCategory => {
              this.activateTab('browse');
              if (topicCategory) this.container.querySelector(`.js-main-chat-option-btn[data-topic-category="${topicCategory}"]`)?.click();
            },
            getSettings: () => this.currentChatSettings(),
            initialFormSettings: this.myPathInitialSettings
          });
        });
      }
      this.container.dispatchEvent(new CustomEvent('my-path:tab', { detail: { active: tabName === 'my_path' } }));
    }

    if (tabName === 'review' && !this.reviewLoaded) {
      this.loadReview();
    } else if (tabName === 'history' && !this.historyLoaded) {
      this.loadHistory(1);
    }
  }

  updateTodaysConversationVisibility() {
    if (!this.todayShell) return;

    this.todayShell.classList.toggle('d-none', this.currentTab !== 'browse' || !this.isBrowseTopLevel);
  }

  bindTodaysConversation() {
    this.todaysLoading = this.container.querySelector('.js-todays-loading');
    this.todaysBody = this.container.querySelector('.js-todays-body');
    this.todaysTitle = this.container.querySelector('.js-todays-title');
    this.todaysDescription = this.container.querySelector('.js-todays-description');
    this.todaysError = this.container.querySelector('.js-todays-error');
    this.todaysStart = this.container.querySelector('.js-todays-start');
    this.todaysStartLabel = this.container.querySelector('.js-todays-start-label');
    this.todaysShuffle = this.container.querySelector('.js-todays-shuffle');

    this.todaysShuffle?.addEventListener('click', () => this.shuffleRecommendation());
    this.todaysStart?.addEventListener('click', () => this.startRecommendation());

    const modeSelect = document.getElementById('chat_form_mode');
    modeSelect?.addEventListener('change', () => this.updateTodaysStartLabel(modeSelect.value));
    this.updateTodaysStartLabel(modeSelect?.value);
  }

  updateTodaysStartLabel(mode) {
    if (!this.todaysStartLabel || !this.todaysStart) return;

    const label = mode === 'call_mode'
      ? this.todaysStart.dataset.startCallLabel
      : this.todaysStart.dataset.startChatLabel;

    if (label) this.todaysStartLabel.textContent = label;
  }

  async loadRecommendation() {
    this.setTodaysLoading(true);

    try {
      const data = await this.api.getRecommendation();
      this.renderRecommendation(data.recommendation);
    } catch (error) {
      this.renderRecommendationError();
    } finally {
      this.setTodaysLoading(false);
    }
  }

  async shuffleRecommendation() {
    if (!this.currentRecommendation) {
      this.loadRecommendation();
      return;
    }

    this.todaysShuffle.disabled = true;

    try {
      const data = await this.api.skipRecommendation(this.currentRecommendation);
      this.renderRecommendation(data.new_recommendation || data.recommendation);
    } catch (error) {
      this.renderRecommendationError();
    } finally {
      this.todaysShuffle.disabled = false;
    }
  }

  startRecommendation() {
    return startWithPhraseIdeas({
      chatKey: this.currentRecommendation?.chat_key,
      title: this.todaysTitle?.textContent
    }, () => this.createRecommendationChat());
  }

  async createRecommendationChat() {
    this.todaysStart.disabled = true;

    try {
      const data = await this.api.startRecommendation(this.currentChatSettings());
      const chat = data.chat || data;

      if (!chat?.id) {
        throw new Error(t('errors.start_chat', 'Unable to start chat'));
      }

      // Call-mode chats live on the call screen, not the chat page.
      const template = chat.mode === 'call_mode' && this.config.paths?.call
        ? this.config.paths.call
        : this.config.paths?.chat;
      window.location.href = replacePathId(template, chat.id);
    } catch (error) {
      window.alert(error.message || t('errors.start_chat', 'Unable to start chat'));
      this.todaysStart.disabled = false;
    }
  }

  currentChatSettings() {
    const form = document.getElementById('new-chat-form');
    if (!form) return {};

    const settings = {};
    new FormData(form).forEach((value, name) => {
      const match = name.match(/^chat_form\[(.+)\]$/);
      if (!match) return;

      settings[match[1]] = value;
    });

    return settings;
  }

  // Start/shuffle stay disabled in DOM snapshots restored by bfcache or
  // Turbolinks after navigating into a chat, so every render re-enables them.
  resetTodaysActionState() {
    if (this.todaysStart) this.todaysStart.disabled = false;
    if (this.todaysShuffle) this.todaysShuffle.disabled = false;
  }

  refreshAfterRestore() {
    this.resetTodaysActionState();
    this.loadRecommendation();
    this.container.dispatchEvent(new CustomEvent('my-path:refresh'));
  }

  renderRecommendation(recommendation) {
    this.currentRecommendation = recommendation;
    this.resetTodaysActionState();
    this.todaysBody?.classList.remove('d-none');
    this.todaysError?.classList.add('d-none');
    this.todaysStart?.classList.remove('d-none');

    const title = recommendation?.title || humanize(recommendation?.chat_key) || t('todays.fallback_title', 'Conversation');
    const description = recommendation?.description || '';

    if (this.todaysTitle) this.todaysTitle.textContent = title;
    if (this.todaysDescription) this.todaysDescription.textContent = description;
  }

  renderRecommendationError() {
    this.currentRecommendation = null;
    this.resetTodaysActionState();
    this.todaysBody?.classList.remove('d-none');
    this.todaysError?.classList.remove('d-none');
    this.todaysStart?.classList.add('d-none');

    if (this.todaysTitle) this.todaysTitle.textContent = t('todays.error_title', 'Unable to load conversation');
    if (this.todaysDescription) this.todaysDescription.textContent = t('todays.error_description', 'Try shuffling again.');
    if (this.todaysError) this.todaysError.textContent = '';
  }

  setTodaysLoading(loading) {
    this.todaysLoading?.classList.toggle('d-none', !loading);
    if (loading) this.todaysBody?.classList.add('d-none');
  }

  async loadReview() {
    const loading = this.container.querySelector('.js-speak-review-loading');
    const content = this.container.querySelector('.js-speak-review-content');
    const empty = this.container.querySelector('.js-speak-review-empty');
    const error = this.container.querySelector('.js-speak-review-error');

    loading?.classList.remove('d-none');
    error?.classList.add('d-none');

    try {
      const [lastChat, grammar] = await Promise.all([
        this.api.getReviewLastChat(),
        this.api.getGrammarRecommendations()
      ]);

      const actionCount = this.renderReviewActions(lastChat);
      const grammarCount = this.renderGrammarRecommendations(grammar.recommendations || []);
      const hasContent = actionCount + grammarCount > 0;

      const showHeaders = actionCount > 0 && grammarCount > 0;
      this.container.querySelector('.js-speak-review-actions-header')?.classList.toggle('d-none', !showHeaders);
      this.container.querySelector('.js-speak-review-grammar-header')?.classList.toggle('d-none', !showHeaders);
      content?.classList.toggle('d-none', !hasContent);
      empty?.classList.toggle('d-none', hasContent);
      this.reviewLoaded = true;
    } catch (loadError) {
      if (error) {
        error.textContent = t('errors.review', 'Unable to load review suggestions.');
        error.classList.remove('d-none');
      }
    } finally {
      loading?.classList.add('d-none');
    }
  }

  renderReviewActions(lastChat) {
    const container = this.container.querySelector('.js-speak-review-actions');
    if (!container) return 0;

    container.textContent = '';
    const cards = [];

    if (lastChat?.chat_id) {
      cards.push({
        icon: 'fa-comments',
        variant: 'chat-speak-action-card--continue',
        title: t('review.continue_title', 'Continue conversation'),
        description: t('review.continue_description', 'Pick up your last chat where you left off.'),
        href: replacePathId(this.config.paths?.chat, lastChat.chat_id)
      });
    }

    if (lastChat?.chat_id && (lastChat.vocab_review || lastChat.has_chat_vocabulary)) {
      cards.push({
        icon: 'fa-book',
        variant: 'chat-speak-action-card--vocab',
        title: t('review.vocab_title', 'Vocab review'),
        description: t('review.vocab_description', 'Practice saved vocabulary from recent chats.'),
        href: replacePathId(this.config.paths?.vocab_review, lastChat.chat_id)
      });
    }

    if (lastChat?.chat_id && lastChat.feedback_report) {
      cards.push({
        icon: 'fa-file-text-o',
        variant: 'chat-speak-action-card--feedback',
        title: t('review.feedback_title', 'Feedback report'),
        description: t('review.feedback_description', 'Review corrections and advice from your latest chat.'),
        href: replacePathId(this.config.paths?.feedback, lastChat.chat_id)
      });
    }

    cards.slice(0, 3).forEach(card => container.appendChild(this.buildActionCard(card)));
    return cards.length;
  }

  renderGrammarRecommendations(recommendations) {
    const container = this.container.querySelector('.js-speak-review-grammar');
    if (!container) return 0;

    container.textContent = '';
    recommendations.slice(0, 5).forEach(recommendation => {
      const column = buildElement('div', 'col-12 col-md-6 chat-speak-card-col');
      const button = buildElement('button', 'chat-speak-action-card chat-speak-action-card--button chat-speak-action-card--grammar chat-speak-action-card--grammar-topic');
      button.type = 'button';

      const icon = buildElement('span', 'chat-speak-action-icon');
      const iconEl = buildElement('i', 'fa fa-pencil');
      iconEl.setAttribute('aria-hidden', 'true');
      icon.appendChild(iconEl);

      const body = buildElement('span', 'chat-speak-action-body');
      const title = buildElement('span', 'chat-speak-action-title');
      title.textContent = recommendation.label;

      body.append(title);
      button.append(icon, body);
      button.addEventListener('click', () => this.openGrammarOptions(recommendation));
      column.appendChild(button);
      container.appendChild(column);
    });

    return recommendations.slice(0, 5).length;
  }

  buildActionCard(card) {
    const column = buildElement('div', 'col-12 col-md-6 chat-speak-card-col');
    const link = buildElement('a', `chat-speak-action-card${card.variant ? ` ${card.variant}` : ''}`);
    link.href = card.href || '#';

    const icon = buildElement('span', 'chat-speak-action-icon');
    const iconEl = buildElement('i', `fa ${card.icon}`);
    iconEl.setAttribute('aria-hidden', 'true');
    icon.appendChild(iconEl);

    const body = buildElement('span', 'chat-speak-action-body');
    body.append(
      buildElement('span', 'chat-speak-action-title', card.title),
      buildElement('span', 'chat-speak-action-description', card.description)
    );

    link.append(icon, body);
    column.appendChild(link);
    return column;
  }

  // Mirror the mobile app: a grammar recommendation opens its options
  // (explainer + exercise list) in Browse rather than starting a chat.
  openGrammarOptions(recommendation) {
    this.activateTab('browse');

    const grammarButton = document.querySelector('.js-main-chat-option-btn[data-topic-category="grammar"]');
    if (!grammarButton) return;
    grammarButton.click();

    const recommendedButton = document.querySelector('.js-sub-category-btn[data-subtopic-category="grammar_recommended"]');
    recommendedButton?.click();

    const label = recommendation.label || recommendation.id;
    const target = Array.from(document.querySelectorAll('.js-sub-category-btn'))
      .find(button => button.dataset.subtopicCategory === `grammar_recommended/${label}`);
    target?.click();
  }

  async loadHistory(page) {
    const loading = this.container.querySelector('.js-speak-history-loading');
    const error = this.container.querySelector('.js-speak-history-error');
    error?.classList.add('d-none');

    const requestId = ++this.historyRequestId;

    // Delay the skeleton so fast responses never flash it; slow ones still
    // get feedback after 250ms.
    const skeletonTimer = setTimeout(() => {
      if (requestId === this.historyRequestId) loading?.classList.remove('d-none');
    }, 250);

    try {
      const data = await this.api.getChats(page, this.historyQuery, this.historyLanguage);
      if (requestId !== this.historyRequestId) return;

      this.historyPage = data.pagination?.current_page || page;
      this.historyTotalPages = data.pagination?.total_pages || this.historyPage;
      this.renderHistoryLanguageFilter(data.languages || []);
      this.renderHistoryRows(data.chats || [], page === 1);
      this.historyLoaded = true;
    } catch (loadError) {
      if (requestId !== this.historyRequestId) return;
      if (error) {
        error.textContent = t('errors.history', 'Unable to load conversation history.');
        error.classList.remove('d-none');
      }
    } finally {
      clearTimeout(skeletonTimer);
      if (requestId === this.historyRequestId) loading?.classList.add('d-none');
    }
  }

  renderHistoryLanguageFilter(languages) {
    const filter = this.container.querySelector('.js-speak-history-language-filter');
    if (!filter) return;

    this.historyLanguages = languages.slice().sort((left, right) => right.count - left.count);
    const visible = shouldShowLanguageFilter(this.historyLanguages);
    filter.classList.toggle('d-none', !visible);
    filter.textContent = '';
    if (!visible) return;

    const options = [{ language: '', label: t('history.all_languages', 'All') }]
      .concat(this.historyLanguages.map(item => ({
        ...item,
        label: this.config.language_labels?.[item.language] || humanize(item.language)
      })));

    options.forEach(option => {
      const active = option.language === this.historyLanguage;
      const button = buildElement('button', `chat-speak-language-filter-btn${active ? ' active' : ''}`);
      button.type = 'button';
      button.dataset.language = option.language;
      button.setAttribute('aria-pressed', active.toString());

      const countryCode = historyFlagCountryCode(
        { language: option.language },
        this.config.language_flag_country_codes
      );
      if (countryCode) {
        const flag = buildElement('span', `fi fi-${countryCode} chat-speak-history-flag`);
        flag.setAttribute('aria-hidden', 'true');
        button.appendChild(flag);
      }
      button.appendChild(document.createTextNode(option.label));
      button.addEventListener('click', () => {
        if (option.language === this.historyLanguage) return;
        this.historyLanguage = option.language;
        this.loadHistory(1);
      });
      filter.appendChild(button);
    });
  }

  renderHistoryRows(chats, reset) {
    const list = this.container.querySelector('.js-speak-history-list');
    const empty = this.container.querySelector('.js-speak-history-empty');
    const loadMore = this.container.querySelector('.js-speak-history-load-more');

    if (!list) return;
    if (reset) {
      list.textContent = '';
      this.loadedHistoryRows = [];
    }

    chats.forEach(chat => {
      const row = this.buildHistoryRow(chat);
      this.loadedHistoryRows.push(row);
      list.appendChild(row);
    });

    empty?.classList.toggle('d-none', this.loadedHistoryRows.length > 0);
    loadMore?.classList.toggle('d-none', this.historyPage >= this.historyTotalPages);
  }

  buildHistoryRow(chat) {
    const row = buildElement('div', 'chat-speak-history-row');
    const titleText = chat.display_topic || chat.title || t('history.untitled', 'Conversation');
    row.dataset.chatId = chat.id;

    const main = buildElement('div', 'chat-speak-history-main');
    const countryCode = historyFlagCountryCode(chat, this.config.language_flag_country_codes);
    if (countryCode) {
      const flag = buildElement('span', `fi fi-${countryCode} chat-speak-history-flag`);
      flag.setAttribute('aria-hidden', 'true');
      main.appendChild(flag);
    }
    const details = buildElement('div', 'chat-speak-history-details');
    const title = buildElement('a', 'chat-speak-history-title', titleText);
    title.href = replacePathId(this.config.paths?.chat, chat.id);
    const meta = buildElement('div', 'chat-speak-history-meta');
    const date = this.formatDate(chat.created_at);
    const count = t('history.message_count', '%{count} messages', { count: chat.user_message_count || 0 });
    meta.textContent = [date, count].filter(Boolean).join(' - ');
    details.append(title, meta);
    main.appendChild(details);

    const actions = this.buildHistoryActions(chat, title);
    row.append(main, actions);
    return row;
  }

  buildHistoryActions(chat, titleEl) {
    const group = buildElement('div', 'dropdown chat-speak-history-actions');
    const button = buildElement('button', 'btn btn-sm btn-primary dropdown-toggle chat-history-actions-btn', t('history.actions', 'Actions'));
    button.type = 'button';
    button.setAttribute('data-toggle', 'dropdown');
    button.setAttribute('aria-haspopup', 'true');
    button.setAttribute('aria-expanded', 'false');

    const menu = buildElement('div', 'dropdown-menu dropdown-menu-right');
    menu.appendChild(this.buildMenuLink(t('history.continue', 'Continue'), replacePathId(this.config.paths?.chat, chat.id), 'fa-comments'));
    menu.appendChild(this.buildMenuLink(t('history.continue_call', 'Continue in Call Mode'), replacePathId(this.config.paths?.call, chat.id), 'fa-phone'));

    // LAN-762: the server decides whether this chat has a feedback report at
    // all (guided lessons and grammar drills do not). Absent on an older
    // payload, which means every chat was eligible then.
    if ((chat.feedback_report_available !== false) && (chat.user_message_count > 5 || chat.has_feedback)) {
      menu.appendChild(this.buildMenuLink(t('history.feedback_report', 'Feedback Report'), replacePathId(this.config.paths?.feedback, chat.id), 'fa-file-text-o'));
    }

    if (chat.has_lesson_content) {
      menu.appendChild(this.buildMenuLink(t('history.review_lesson', 'Review What You Learned'), replacePathId(this.config.paths?.lesson_progress, chat.id), 'fa-graduation-cap'));
    }

    menu.appendChild(this.buildMenuButton(t('history.rename', 'Rename'), 'fa-pencil', () => this.renameChat(chat, titleEl)));
    menu.appendChild(this.buildMenuButton(t('history.delete', 'Delete'), 'fa-trash', () => this.deleteChat(chat), true));
    group.append(button, menu);
    return group;
  }

  buildMenuLink(label, href, iconClass) {
    const link = buildElement('a', 'dropdown-item');
    link.href = href;
    link.append(this.buildMenuIcon(iconClass), document.createTextNode(label));
    return link;
  }

  buildMenuButton(label, iconClass, onClick, dangerous = false) {
    const button = buildElement('button', `dropdown-item${dangerous ? ' text-danger' : ''}`);
    button.type = 'button';
    button.append(this.buildMenuIcon(iconClass), document.createTextNode(label));
    button.addEventListener('click', onClick);
    return button;
  }

  buildMenuIcon(iconClass) {
    const icon = buildElement('i', `fa ${iconClass} mr-2`);
    icon.setAttribute('aria-hidden', 'true');
    return icon;
  }

  async renameChat(chat, titleEl) {
    const currentTitle = titleEl.textContent;
    const title = window.prompt(t('history.rename_prompt', 'Rename this conversation'), currentTitle);
    if (!title || title === currentTitle) return;

    try {
      const data = await this.api.updateChatTitle(chat.id, title);
      titleEl.textContent = data.display_topic || title;
    } catch (error) {
      window.alert(error.message || t('errors.rename', 'Unable to rename conversation.'));
    }
  }

  async deleteChat(chat) {
    if (!window.confirm(t('history.delete_confirm', 'Are you sure you want to delete this chat?'))) return;

    try {
      await this.api.deleteChat(chat.id);
      const row = this.loadedHistoryRows.find(historyRow => historyRow.dataset.chatId === String(chat.id));
      if (row) {
        row.remove();
        this.loadedHistoryRows = this.loadedHistoryRows.filter(historyRow => historyRow !== row);
      }
      this.container.querySelector('.js-speak-history-empty')?.classList.toggle('d-none', this.loadedHistoryRows.length > 0);
    } catch (error) {
      window.alert(error.message || t('errors.delete', 'Unable to delete conversation.'));
    }
  }


  formatDate(value) {
    if (!value) return '';

    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(value));
  }

  bindVoicePicker() {
    this.voiceMenu = this.container.querySelector('.js-speak-voice-menu');
    this.voiceName = this.container.querySelector('.js-speak-current-voice-name');

    const picker = this.container.querySelector('.chat-speak-partner-picker');
    if (picker && window.$) {
      window.$(picker).on('hidden.bs.dropdown', () => this.stopVoiceSample());
    }

    const modalVoiceSelect = document.getElementById('chat_form_voice');
    modalVoiceSelect?.addEventListener('change', () => this.syncCurrentVoiceFromForm(modalVoiceSelect.value));
  }

  loadVoiceOptions() {
    if (!this.voiceMenu) return;
    this.voiceMenu.textContent = '';
    const voices = this.sortVoices(this.config.voices || []);
    this.renderVoiceOptions(voices);
  }

  // Backend label rank: recommended voices first, legacy ones last. Only
  // Japanese voices carry labels today, so other languages stay alphabetical.
  static voiceLabelRank(label) {
    if (label === 'recommended') return 0;
    if (label === 'legacy') return 2;
    return 1;
  }

  sortVoices(voices) {
    return voices.slice().sort((a, b) => {
      const rankDiff = SpeakPageService.voiceLabelRank(a.label) - SpeakPageService.voiceLabelRank(b.label);
      if (rankDiff !== 0) return rankDiff;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }

  renderVoiceOptions(voices) {
    this.stopVoiceSample();
    this.voiceMenu.textContent = '';

    if (voices.length === 0) {
      this.voiceMenu.appendChild(buildElement('div', 'px-3 py-2 text-muted small', t('partner.empty', 'No voices available.')));
      return;
    }

    voices.forEach(voice => {
      // A div rather than a button: each row hosts a nested play button, and
      // interactive content inside a <button> is invalid HTML.
      const button = buildElement('div', 'dropdown-item chat-speak-voice-option');
      button.setAttribute('role', 'button');
      button.tabIndex = 0;
      button.dataset.voiceId = voice.id;
      if (voice.id === this.currentVoiceId) button.classList.add('selected');

      const avatar = voice.avatar_url || !this.selfHostedAssets
        ? buildElement('img', 'chat-speak-voice-option-avatar')
        : buildElement('span', 'chat-speak-voice-option-avatar chat-speak-avatar--placeholder', String(voice.name || '?').charAt(0));
      if (voice.avatar_url || !this.selfHostedAssets) {
        avatar.src = voice.avatar_url;
        avatar.alt = '';
        avatar.loading = 'lazy';
      } else {
        avatar.setAttribute('aria-hidden', 'true');
      }

      const name = buildElement('span', 'chat-speak-voice-option-name', voice.name);
      const tags = buildElement('span', 'chat-speak-voice-tags');

      // A backend label (recommended / legacy) is the single grey tag for the
      // voice; the speed hint is only shown for voices without one.
      const label = String(voice.label || '').toLowerCase();
      if (label) {
        const labelTag = buildElement('span', 'chat-speak-voice-tag chat-speak-voice-tag--label', t(`partner.label_${label}`, label));
        tags.appendChild(labelTag);
      } else {
        const speed = String(voice.speed || '').toLowerCase();
        if (speed && speed !== 'normal') {
          tags.appendChild(buildElement('span', 'chat-speak-voice-tag chat-speak-voice-tag--speed', speed));
        }
      }

      if (voice.provider === 'openai') {
        const providerTag = buildElement('span', 'chat-speak-voice-tag chat-speak-voice-tag--v2', 'v2');
        providerTag.title = t('partner.openai_v2', 'Newer voice technology');
        tags.appendChild(providerTag);
      }

      const gender = String(voice.gender || '').toLowerCase();
      if (gender) {
        const female = gender === 'female';
        const genderTag = buildElement('span', `chat-speak-voice-tag chat-speak-voice-tag--${female ? 'f' : 'm'}`, female ? 'F' : 'M');
        genderTag.title = voice.gender;
        tags.appendChild(genderTag);
      }

      if (voice.country_code) {
        const flag = buildElement('span', `fi fi-${String(voice.country_code).toLowerCase()} chat-speak-voice-flag`);
        flag.title = voice.country || '';
        tags.appendChild(flag);
      }

      button.append(avatar, name, tags, this.buildVoicePreviewButton(voice));
      button.addEventListener('click', event => {
        if (event.target.closest('.chat-speak-voice-preview')) return;

        this.selectVoice(voice);
      });
      button.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        this.selectVoice(voice);
      });
      this.voiceMenu.appendChild(button);
    });
  }

  buildVoicePreviewButton(voice) {
    const button = buildElement('button', 'chat-speak-voice-preview');
    button.type = 'button';
    button.setAttribute('aria-label', t('partner.preview', 'Play a sample of this voice'));

    const icon = buildElement('i', 'fa fa-play');
    icon.setAttribute('aria-hidden', 'true');
    button.appendChild(icon);

    button.addEventListener('click', event => {
      // Playing a sample must not select the voice or close the dropdown.
      event.preventDefault();
      event.stopPropagation();
      this.toggleVoiceSample(voice, button);
    });

    return button;
  }

  async toggleVoiceSample(voice, button) {
    if (this.activeSampleVoiceId === voice.id) {
      this.stopVoiceSample();
      return;
    }

    this.stopVoiceSample();
    const requestId = ++this.sampleRequestId;
    this.activeSampleVoiceId = voice.id;
    this.activeSampleButton = button;
    this.setSampleButtonState(button, 'loading');

    try {
      const url = await this.voiceSampleUrl(voice);
      if (requestId !== this.sampleRequestId) return;

      // Cartus reuses the sample JSON contract without consumer CDN storage.
      // Convert inline MP3 to a local blob allowed by the existing media CSP.
      let playbackUrl = url;
      if (this.privateVoiceSamples && url.startsWith('data:audio/mpeg;base64,')) {
        const encoded = url.slice('data:audio/mpeg;base64,'.length);
        if (encoded.length > 12 * 1024 * 1024) throw new Error('Voice sample too large.');
        const bytes = Uint8Array.from(atob(encoded), char => char.charCodeAt(0));
        playbackUrl = URL.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }));
        this.sampleBlobUrl = playbackUrl;
      }
      const audio = new Audio(playbackUrl);
      this.sampleAudio = audio;
      audio.addEventListener('ended', () => {
        if (this.sampleAudio === audio) this.stopVoiceSample();
      });
      audio.addEventListener('error', () => {
        if (this.sampleAudio === audio) this.markSampleUnavailable(button);
      });
      await audio.play();
      if (this.sampleAudio === audio) this.setSampleButtonState(button, 'playing');
    } catch (error) {
      console.warn('Unable to play voice sample', error);
      if (requestId === this.sampleRequestId) this.markSampleUnavailable(button);
    }
  }

  // A sample failed to generate or play (missing provider key, provider
  // outage, decode error). Surface it on the button rather than snapping
  // silently back to the play icon, and leave the button clickable so the
  // learner can retry.
  markSampleUnavailable(button) {
    if (this.privateVoiceSamples) this.stopVoiceSample();
    this.sampleAudio = null;
    this.activeSampleButton = null;
    this.activeSampleVoiceId = null;
    this.setSampleButtonState(button, 'error');
  }

  stopVoiceSample() {
    this.sampleRequestId += 1;

    if (this.sampleAudio) {
      this.sampleAudio.pause();
      this.sampleAudio = null;
    }

    if (this.sampleBlobUrl) {
      URL.revokeObjectURL(this.sampleBlobUrl);
      this.sampleBlobUrl = null;
    }

    if (this.activeSampleButton) {
      this.setSampleButtonState(this.activeSampleButton, 'idle');
    }
    this.activeSampleButton = null;
    this.activeSampleVoiceId = null;
  }

  // The voice menu's Latest | Standard choice (LAN-1654), so a sample played
  // after switching is heard in that version rather than from the cache.
  get voiceVersion() {
    return document.querySelector?.('.chat-speak-voice-menu .js-voice-version-toggle')?.dataset.version || null;
  }

  async voiceSampleUrl(voice) {
    const voiceVersion = this.voiceVersion;
    const voiceKey = this.privateVoiceSamples ? `${this.currentLanguage}:${voice.id}` : voice.id;
    const cacheKey = voiceVersion ? `${voiceKey}:${voiceVersion}` : voiceKey;
    if (!this.sampleUrlCache[cacheKey]) {
      const response = await this.api.getVoiceSample(voice, this.currentLanguage, voiceVersion);
      const url = response?.data?.sample_url;
      if (!url) throw new Error('Voice sample unavailable.');

      this.sampleUrlCache[cacheKey] = url;
    }

    return this.sampleUrlCache[cacheKey];
  }

  setSampleButtonState(button, state) {
    const icon = button.querySelector('i');
    if (!icon) return;

    const icons = {
      idle: 'fa fa-play',
      loading: 'fa fa-circle-o-notch fa-spin',
      playing: 'fa fa-stop',
      error: 'fa fa-exclamation-circle'
    };
    icon.className = icons[state] || icons.idle;
    button.classList.toggle('is-playing', state === 'playing');
    button.classList.toggle('is-unavailable', state === 'error');

    const labels = {
      playing: t('partner.preview_stop', 'Stop the voice sample'),
      error: t('partner.preview_unavailable', 'Sample unavailable — tap to retry')
    };
    const label = labels[state] || t('partner.preview', 'Play a sample of this voice');
    button.setAttribute('aria-label', label);
    button.title = state === 'error' ? label : '';
  }

  async selectVoice(voice) {
    const payload = {
      team: {
        chat_settings: {
          voice: voice.id,
          voice_provider: PROVIDER_VERSIONS[voice.provider] || 'v1'
        }
      }
    };

    const mappedDialect = this.preferredDialectForVoice(voice);
    if (mappedDialect) {
      payload.team.preferred_dialect = mappedDialect;
    }

    try {
      await this.api.updateLanguaSettings(payload);
      if (mappedDialect) this.preferredDialect = mappedDialect;
      this.currentVoiceId = voice.id;
      this.voiceMenu?.querySelectorAll('.chat-speak-voice-option').forEach(option => {
        option.classList.toggle('selected', option.dataset.voiceId === voice.id);
      });
      this.syncVoiceSelection(voice);
      this.updateCurrentVoiceDisplay(voice);
    } catch (error) {
      window.alert(error.message || t('errors.voice_update', 'Unable to update voice.'));
    }
  }

  // Decided on the server from the voice's country (VoiceOptionsService).
  preferredDialectForVoice(voice) {
    return voice.preferred_dialect || null;
  }

  syncCurrentVoiceFromForm(voiceId) {
    const voice = (this.config.voices || []).find(option => option.id === voiceId || option.value === voiceId);
    if (voice) {
      this.currentVoiceId = voice.id || voice.value;
      this.updateCurrentVoiceDisplay(voice);
      this.voiceMenu?.querySelectorAll('.chat-speak-voice-option').forEach(option => {
        option.classList.toggle('selected', option.dataset.voiceId === this.currentVoiceId);
      });
    } else if (voiceId) {
      this.currentVoiceId = voiceId;
      this.updateCurrentVoiceDisplay({ id: voiceId, name: humanize(voiceId), avatar_url: null });
    }
  }

  syncVoiceSelection(voice) {
    // Provider radio first: its change handler repopulates the voice select
    // in call mode, which would clobber a voice value set beforehand.
    const providerValue = PROVIDER_VERSIONS[voice.provider];
    if (providerValue) {
      const input = document.querySelector(`input[name="chat_form[voice_provider]"][value="${providerValue}"]`);
      if (input && !input.checked) {
        document.querySelectorAll('input[name="chat_form[voice_provider]"]').forEach(providerInput => {
          providerInput.checked = false;
          providerInput.closest('label')?.classList.remove('active');
        });
        input.checked = true;
        input.closest('label')?.classList.add('active');
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const select = document.getElementById('chat_form_voice');
    if (select) {
      const existingOption = Array.from(select.options).find(option => option.value === voice.id);
      if (!existingOption) {
        const option = document.createElement('option');
        option.value = voice.id;
        option.textContent = voice.name;
        select.appendChild(option);
      }
      select.value = voice.id;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateCurrentVoiceDisplay(voice) {
    const name = voice.name || voice.label || humanize(voice.id);
    if (this.voiceName) this.voiceName.textContent = name;

    const existingAvatar = this.container.querySelector('.js-speak-current-voice-avatar, .js-speak-current-voice-avatar-placeholder');
    if (!existingAvatar) return;

    if (voice.avatar_url) {
      const avatar = buildElement('img', 'chat-speak-avatar js-speak-current-voice-avatar');
      avatar.src = voice.avatar_url;
      avatar.alt = '';
      existingAvatar.replaceWith(avatar);
      return;
    }

    const placeholder = buildElement('span', 'chat-speak-avatar chat-speak-avatar--placeholder js-speak-current-voice-avatar-placeholder', name.charAt(0) || '?');
    placeholder.setAttribute('aria-hidden', 'true');
    existingAvatar.replaceWith(placeholder);
  }

  bindLanguageModal() {
    this.languageInputs = Array.from(document.querySelectorAll('.js-speak-language-input'));
    this.levelInputs = Array.from(document.querySelectorAll('.js-speak-level-input'));
    this.wizardBack = document.querySelector('.js-speak-wizard-back');
    this.wizardTitle = document.querySelector('.js-speak-wizard-title');
    this.wizardSteps = Array.from(document.querySelectorAll('.js-speak-step'));
    this.dialectList = document.querySelector('.js-speak-dialect-list');
    this.languageError = document.querySelector('.js-speak-language-error');
    this.languageSaving = document.querySelector('.js-speak-language-saving');
    this.wizardSelection = {
      language: this.currentLanguage,
      dialect: this.preferredDialect,
      level: this.config.stream_language_level
    };
    this.wizardSaving = false;

    // Clicking a label always fires 'click' but only fires 'change' when the
    // radio was unchecked — users changing only dialect/level click their
    // already-selected language, so advance on click; keep 'change' for
    // keyboard (arrow-key) selection.
    this.languageInputs.forEach(input => {
      input.addEventListener('change', () => this.chooseLanguage(input.value));
      input.closest('.chat-speak-language-option')?.addEventListener('click', () => {
        if (input.checked) this.chooseLanguage(input.value);
      });
    });

    this.levelInputs.forEach(input => {
      input.addEventListener('change', () => this.chooseLevel(input.value));
      input.closest('.js-speak-level-option')?.addEventListener('click', () => {
        if (input.checked) this.chooseLevel(input.value);
      });
    });

    this.wizardBack?.addEventListener('click', () => this.wizardBackStep());

    const languageSearch = document.querySelector('.js-speak-language-search');
    languageSearch?.addEventListener('input', () => {
      const query = languageSearch.value.toLowerCase().trim();
      this.languageInputs.forEach(input => {
        const option = input.closest('.chat-speak-language-option');
        if (!option) return;
        const label = option.querySelector('.chat-speak-language-option-label')?.textContent?.toLowerCase() || '';
        option.classList.toggle('d-none', Boolean(query) && !label.includes(query));
      });
    });

    if (window.$) {
      window.$('#speakLanguageModal').on('show.bs.modal', () => this.resetWizard());
    }
  }

  resetWizard() {
    if (this.wizardSaving) return;

    this.wizardSelection = {
      language: this.currentLanguage,
      dialect: this.preferredDialect,
      level: this.config.stream_language_level
    };

    this.languageInputs.forEach(input => {
      input.checked = input.value === this.wizardSelection.language;
    });
    this.levelInputs.forEach(input => {
      input.checked = input.value === this.wizardSelection.level;
    });
    this.languageError?.classList.add('d-none');
    this.showWizardStep('language');
  }

  dialectOptionsFor(language) {
    return this.config.dialect_options?.[language] || [];
  }

  chooseLanguage(language) {
    if (this.wizardSaving) return;

    this.wizardSelection.language = language;

    const dialects = this.dialectOptionsFor(language);
    if (dialects.length > 0) {
      this.renderDialectStep(dialects);
      this.showWizardStep('dialect');
    } else {
      this.wizardSelection.dialect = null;
      this.wizardSelection.dialectLabel = null;
      this.showWizardStep('level');
    }
  }

  renderDialectStep(dialects) {
    if (!this.dialectList) return;

    const flags = {
      american_english: 'us',
      british_english: 'gb',
      brazilian_portuguese: 'br',
      european_portuguese: 'pt',
      latin_american_spanish: 'mx',
      castilian_spanish: 'es',
      french_france: 'fr',
      french_canada: 'ca',
      dutch: 'nl',
      flemish: 'be'
    };

    const currentDialect = this.wizardSelection.language === this.currentLanguage ? this.preferredDialect : null;
    this.dialectList.textContent = '';

    dialects.forEach(([label, value]) => {
      const button = buildElement('button', 'chat-speak-dialect-option');
      button.type = 'button';
      if (value === currentDialect) button.classList.add('selected');

      const flagCode = flags[value];
      if (flagCode) {
        button.appendChild(buildElement('span', `fi fi-${flagCode} chat-speak-dialect-flag`));
      }
      button.appendChild(buildElement('span', 'chat-speak-dialect-label', label));
      button.addEventListener('click', () => {
        this.wizardSelection.dialect = value;
        this.wizardSelection.dialectLabel = label;
        this.showWizardStep('level');
      });
      this.dialectList.appendChild(button);
    });
  }

  buildWizardContext(includeDialect) {
    const chip = buildElement('span', 'chat-speak-wizard-chip');
    const selectedInput = this.languageInputs.find(input => input.value === this.wizardSelection.language);
    const option = selectedInput?.closest('.chat-speak-language-option');
    const flag = option?.querySelector('.chat-speak-language-option-flag')?.firstElementChild;
    if (flag) chip.appendChild(flag.cloneNode(true));

    let label = option?.querySelector('.chat-speak-language-option-label')?.textContent || this.wizardSelection.language;
    if (includeDialect && this.wizardSelection.dialectLabel) {
      label = `${label} \u00b7 ${this.wizardSelection.dialectLabel}`;
    }
    chip.appendChild(buildElement('span', 'chat-speak-wizard-chip-label', label));
    return chip;
  }

  showWizardStep(step) {
    this.wizardSteps.forEach(el => {
      el.classList.toggle('d-none', el.dataset.speakStep !== step);
    });

    this.wizardBack?.classList.toggle('d-none', step === 'language');

    if (this.wizardTitle) {
      const titles = {
        language: t('language_wizard.language_title', 'Language settings'),
        dialect: t('language_wizard.dialect_title', 'Select dialect'),
        level: t('language_wizard.level_title', 'Select your level')
      };
      this.wizardTitle.textContent = titles[step];
    }

    if (step === 'dialect') {
      const context = document.querySelector('.js-speak-context-dialect');
      if (context) {
        context.textContent = '';
        context.appendChild(this.buildWizardContext(false));
      }
    }

    if (step === 'level') {
      const context = document.querySelector('.js-speak-context-level');
      if (context) {
        context.textContent = '';
        context.appendChild(this.buildWizardContext(true));
      }
    }
  }

  wizardBackStep() {
    const current = this.wizardSteps.find(el => !el.classList.contains('d-none'))?.dataset.speakStep;
    if (current === 'level' && this.dialectOptionsFor(this.wizardSelection.language).length > 0) {
      this.showWizardStep('dialect');
    } else if (current === 'dialect' || current === 'level') {
      this.showWizardStep('language');
    }
  }

  async chooseLevel(level) {
    if (this.wizardSaving) return;

    this.wizardSelection.level = level;
    this.wizardSaving = true;
    this.languageError?.classList.add('d-none');
    this.languageSaving?.classList.remove('d-none');
    this.levelInputs.forEach(input => { input.disabled = true; });

    const team = {
      stream_language: this.wizardSelection.language,
      stream_language_level: level,
      preferred_dialect: this.wizardSelection.dialect || null
    };

    try {
      await this.api.updateLanguaSettings({ team });
      window.location.reload();
    } catch (error) {
      this.wizardSaving = false;
      this.languageSaving?.classList.add('d-none');
      this.levelInputs.forEach(input => { input.disabled = false; });
      if (this.languageError) {
        this.languageError.textContent = error.message || t('errors.language_update', 'Unable to update language settings.');
        this.languageError.classList.remove('d-none');
      }
    }
  }

}

let currentService = null;

document.addEventListener('turbolinks:load', () => {
  const container = document.querySelector('.chat-content[data-speak-page="true"]');
  currentService = container ? new SpeakPageService(container) : null;
});

document.addEventListener('turbolinks:before-cache', () => {
  if (currentService) currentService.destroyed = true;
  currentService?.unmountMyPath?.();
  currentService?.stopVoiceSample();
  currentService?.resetTodaysActionState();
  currentService = null;
});

// Starting a chat navigates away with the Today buttons disabled; when the
// browser restores that page from bfcache the stale DOM and recommendation
// come back with it, so refetch instead of trusting the snapshot.
window.addEventListener('pageshow', event => {
  if (event.persisted) currentService?.refreshAfterRestore();
});

export default SpeakPageService;
